// frontend/src/Components/PixBimPath.tsx
// TOTALT OPTIMERAD – för extrem prestanda i stora planer

import React, { useCallback, memo } from "react";
import { extend } from "@pixi/react";
import { Graphics, Texture } from "pixi.js";

import { IPixPath } from "../types";

// Skapa texture EN GÅNG för hela applikationen
const WHITE_TEXTURE = Texture.WHITE;
WHITE_TEXTURE.source.scaleMode = "nearest";

extend({ Graphics });

interface PixBimPathProps {
  path: IPixPath;
}

// Jämför bara det som påverkar rendering – extremt snabbt
const arePropsEqual = (prev: PixBimPathProps, next: PixBimPathProps): boolean => {
  const p = prev.path;
  const n = next.path;

  // Snabb referensjämförelse först
  if (p === n) return true;
  if (p.points === n.points && p.strokeColor === n.strokeColor && p.strokeWidth === n.strokeWidth) {
    return true;
  }

  // Fallback: djup jämförelse bara om nödvändigt
  return (
    p.strokeColor === n.strokeColor &&
    p.strokeWidth === n.strokeWidth &&
    p.points.length === n.points.length &&
    p.points.every((point, i) => point[0] === n.points[i][0] && point[1] === n.points[i][1])
  );
};

const PixBimPath = memo(({ path }: PixBimPathProps) => {
  const { points, strokeColor = 0x000000 } = path;

  // Konvertera till number direkt (PIXI vill ha number, inte string)
  const strokeColorNum =
    typeof strokeColor === "string" ? parseInt(strokeColor.replace("#", ""), 16) : strokeColor;

  // Optimerad strokeWidth – reaktiv och stabil
  const strokeWidth = React.useMemo(() => {
    if (!path.strokeWidth) return 1;
    if (typeof path.strokeWidth === "string") {
      const num = parseFloat(path.strokeWidth.replace("px", ""));
      return isNaN(num) ? 1 : num;
    }
    return path.strokeWidth;
  }, [path.strokeWidth]);

  const draw = useCallback(
    (g: Graphics) => {
      g.clear();

      if (points.length < 2) return;

      const [firstX, firstY] = points[0]!;
      let x = firstX;
      let y = firstY;

      g.moveTo(x, y);

      // for-loop är snabbast i V8 (bättre än forEach, for...of)
      for (let i = 1; i < points.length; i++) {
        const [px, py] = points[i]!;
        g.lineTo(px, py);
      }

      g.stroke({
        width: strokeWidth,
        color: strokeColorNum,
        texture: WHITE_TEXTURE,
      });
    },
    [points, strokeWidth, strokeColorNum] // Perfekta dependencies
  );

  return <graphics draw={draw} />;
}, arePropsEqual);

PixBimPath.displayName = "PixBimPath";

export default React.memo(PixBimPath);
